import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Button, Card, CardContent, CircularProgress, Divider, Grid, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import StatusChip from "../components/StatusChip";
import { OrderVM, OrderStatus } from "../data/types";
import { toOrderVM } from "../data/mappers";
import { formatCAD } from "../utils/currency";
import { fetchOrder, updateOrderStatus, setOrderNotes } from "../data/api";
import { useAuth } from "../auth/AuthContext";

const NEXT: Record<OrderStatus, OrderStatus[]> = {
  New: ["Confirmed", "Cancelled"],
  Pending: ["Confirmed", "Cancelled"],
  Confirmed: ["Preparing", "Cancelled"],
  Preparing: ["Delivered"],
  Delivered: [],
  Cancelled: [],
};
const ACTION_LABEL: Record<string, string> = {
  Confirmed: "Confirm order", Preparing: "Start preparing",
  Delivered: "Mark delivered", Cancelled: "Cancel order",
};
const fmt = (d?: string) => (d ? dayjs(d).format("MMM D, YYYY") : "—");
const fmtTime = (d?: string) => (d ? dayjs(d).format("MMM D, YYYY h:mm A") : "—");

interface Props {
  /** Distributor view gets status controls + internal notes; buyer view is read-only. */
  manage?: boolean;
  backPath: string;
  /** Label for the distributor column (buyer sees their distributor's name). */
  distributorLabel?: string;
}

const OrderDetailPage: React.FC<Props> = ({ manage: manageProp, backPath, distributorLabel }) => {
  const { id } = useParams();
  const { impersonating } = useAuth();
  const manage = !!manageProp && !impersonating; // read-only during tenant inspection
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderVM | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    const n = Number(id);
    if (!Number.isFinite(n)) { setNotFound(true); setLoading(false); return; }
    try {
      const raw = await fetchOrder(n);
      if (!raw) { setNotFound(true); return; }
      const vm = toOrderVM(raw);
      setOrder(vm);
      setNotes(vm.internalNotes || "");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (next: OrderStatus) => {
    if (!order) return;
    setBusy(next); setErr(null);
    try {
      await updateOrderStatus(order.id, next);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.response?.data?.error || "Could not update the order status.");
    } finally { setBusy(null); }
  };

  const saveNotes = async () => {
    if (!order) return;
    setSavingNotes(true); setErr(null);
    try { await setOrderNotes(order.id, notes); await load(); }
    catch (e: any) { setErr(e?.response?.data?.message || "Could not save notes."); }
    finally { setSavingNotes(false); }
  };

  if (loading) return <Box sx={{ display: "grid", placeItems: "center", py: 10 }}><CircularProgress /></Box>;

  if (notFound || !order) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: "auto" }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)} sx={{ mb: 2 }}>Back to Orders</Button>
        <Alert severity="warning">Order not found, or you don't have access to it.</Alert>
      </Box>
    );
  }

  const nexts = manage ? (NEXT[order.status] ?? []) : [];
  const subtotal = order.subtotal ?? (order.lines?.reduce((s, l) => s + l.lineTotal, 0) ?? 0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)}>Back to Orders</Button>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{order.orderId}</Typography>
          <StatusChip status={order.status} />
        </Stack>
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}

      <Grid container spacing={2}>
        {/* Parties + delivery */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: "100%" }}><CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Order information</Typography>
            <Stack spacing={0.75}>
              <Line label="Customer" value={order.customer || "—"} />
              <Line label="Distributor" value={distributorLabel || (order.distributorId ? `#${order.distributorId}` : "—")} />
              <Line label="Order date" value={fmt(order.orderDate)} />
              <Line label="Requested delivery" value={fmt(order.deliveryDate)} />
              <Line label="Items" value={String(order.items)} />
            </Stack>
          </CardContent></Card>
        </Grid>

        {/* Addresses */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: "100%" }}><CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Billing &amp; delivery</Typography>
            <Typography variant="body2" color="text.secondary">
              No billing or delivery address was captured for this order. Address capture at checkout
              is a tracked follow-up; the requested delivery date above applies.
            </Typography>
          </CardContent></Card>
        </Grid>

        {/* Line items */}
        <Grid size={{ xs: 12 }}>
          <Card><CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Products</Typography>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Packaging</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit price</TableCell>
                    <TableCell align="right">Line total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(order.lines || []).map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>{l.productName}</TableCell>
                      <TableCell>{l.sku || "—"}</TableCell>
                      <TableCell>{l.packagingUnit || "—"}</TableCell>
                      <TableCell align="right">{l.quantity}</TableCell>
                      <TableCell align="right">{formatCAD(l.unitPrice)}</TableCell>
                      <TableCell align="right">{formatCAD(l.lineTotal)}</TableCell>
                    </TableRow>
                  ))}
                  {(!order.lines || order.lines.length === 0) && (
                    <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No line items recorded.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <Stack spacing={0.5} sx={{ ml: "auto", maxWidth: 280 }}>
              <Line label="Subtotal" value={formatCAD(subtotal)} />
              {order.tax != null && <Line label="Tax (HST)" value={formatCAD(order.tax)} />}
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Grand total (CAD)</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatCAD(order.total)}</Typography>
              </Stack>
            </Stack>
          </CardContent></Card>
        </Grid>

        {/* Status history */}
        <Grid size={{ xs: 12, md: manage ? 6 : 12 }}>
          <Card sx={{ height: "100%" }}><CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Status history</Typography>
            {order.statusHistory && order.statusHistory.length > 0 ? (
              <Stack spacing={1}>
                {order.statusHistory.map((h, i) => (
                  <Stack key={i} direction="row" spacing={1.5} alignItems="baseline">
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 150 }}>{fmtTime(h.changedAt)}</Typography>
                    <Typography variant="body2">
                      {h.fromStatus ? `${h.fromStatus} → ` : ""}<b>{h.toStatus}</b>{h.note ? ` — ${h.note}` : ""}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Current status: <b>{order.status}</b>. No change history recorded yet.
              </Typography>
            )}
          </CardContent></Card>
        </Grid>

        {/* Distributor: status transitions, fulfillment + internal notes */}
        {manage && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: "100%" }}><CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Fulfillment</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {nexts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No further status changes available ({order.status}).</Typography>
                ) : nexts.map((n) => (
                  <Button key={n} variant={n === "Cancelled" ? "outlined" : "contained"}
                    color={n === "Cancelled" ? "error" : "primary"} disabled={busy !== null}
                    onClick={() => changeStatus(n)}>
                    {busy === n ? <CircularProgress size={18} color="inherit" /> : (ACTION_LABEL[n] || n)}
                  </Button>
                ))}
              </Stack>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Internal notes</Typography>
              <TextField multiline minRows={3} fullWidth size="small" value={notes}
                onChange={(e) => setNotes(e.target.value)} placeholder="Notes visible to distributor staff only" />
              <Button sx={{ mt: 1 }} variant="outlined" size="small" disabled={savingNotes} onClick={saveNotes}>
                {savingNotes ? <CircularProgress size={16} /> : "Save notes"}
              </Button>
            </CardContent></Card>
          </Grid>
        )}

        {/* Buyer: show notes only if present */}
        {!manage && order.internalNotes && (
          <Grid size={{ xs: 12 }}>
            <Card><CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Notes</Typography>
              <Typography variant="body2">{order.internalNotes}</Typography>
            </CardContent></Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const Line: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>{value}</Typography>
  </Stack>
);

export default OrderDetailPage;
